/* reason: better readability to put get and set together for the same variable */
/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable, afterNextRender, signal, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Device, LoginResponse } from '../../features/authentication/models/authentication.model';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
// import { SsrStateService } from './ssr-state.service';

@Injectable({
    providedIn: 'root',
})
export class StorageService {
    private localStorageCache = new Map<string, string>();
    private isClientSide = signal(false);

    constructor(
        private cookieService: SsrCookieService,
        // No longer in use from "getFromStorage" and "setToStorage".
        //private ssrStateService: SsrStateService
    ) {
        // Consolidated client-side initialization.
        // This callback does not get called from server-side in SSR.
        afterNextRender(() => {
            // Developer Note: We require this flag as we need to prevent
            // local storage CRUD operations from server-side in SSR.
            this.isClientSide.set(true);
            this.syncFromLocalStorage();
        });
    }

    redirectRoute: string = "";

    private syncFromLocalStorage(): void {
        if (this.isClientSide() && typeof localStorage !== 'undefined') {
            // Sync existing localStorage values to cache
            const keys = ['id', 'registrationId', 'deviceNo', 'userName', 'rememberMe', 'language', 'lastCheckedDate', 'deviceRemovalAuthorizeToken', 'taxpod_clip_visitor_id'];
            keys.forEach(key => {
                const value = localStorage.getItem(key);
                if (value !== null) {
                    this.localStorageCache.set(key, value);
                }
            });
        }
    }

    private getFromStorage(key: string): string {
        // Check TransferState first for language during SSR/hydration
        // Developer Note: Commented out because we'll always either to client local storage for browser.
        // This is irrelevant for server-side.
        // if (key === 'language' && this.ssrStateService.hasLanguage()) {
        //     return this.ssrStateService.getLanguage() || '';
        // }
        
        // For simple reads, use direct browser check instead of afterRender
        if (this.isClientSide() && typeof localStorage !== 'undefined') {
            return localStorage.getItem(key) || '';
        }
        return this.localStorageCache.get(key) || '';
    }

    private setToStorage(key: string, value: string): void {
        this.localStorageCache.set(key, value);
        
        // Store language in TransferState for SSR/hydration
        // Developer Note: Commented out because we'll always either to client local storage for browser.
        // This is irrelevant for server-side.
        // if (key === 'language') {
        //     this.ssrStateService.setLanguage(value);
        // }
        
        // Use direct browser check for simple localStorage operations
        if (this.isClientSide() && typeof localStorage !== 'undefined') {
            localStorage.setItem(key, value);
        }
    }

    private removeFromStorage(key: string): void {
        this.localStorageCache.delete(key);
        // Use direct browser check for simple localStorage operations
        if (this.isClientSide() && typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
    }

    get accessToken(): string {
        return this.cookieService.get('accessToken') || '';
    }

    set accessToken(accessToken: string) {
        this.cookieService.set('accessToken', accessToken || '', 2, '/', '', true, 'Strict');
    }

    get refreshToken(): string {
        return this.cookieService.get('refreshToken') || '';
    }

    set refreshToken(refreshToken: string) {
        this.cookieService.set('refreshToken', refreshToken || '', 90, '/', '', true, 'Strict');
    }

    get userId(): string {
        return this.getFromStorage('id');
    }

    set userId(userId: string) {
        this.setToStorage('id', userId || '');
    }

    get registrationId(): string {
        return this.getFromStorage('registrationId');
    }

    set registrationId(registrationId: string) {
        this.setToStorage('registrationId', registrationId || '');
    }

    get deviceNo(): string {
        return this.getFromStorage('deviceNo');
    }

    set deviceNo(deviceNo: string) {
        this.setToStorage('deviceNo', deviceNo || '');
    }

    get userName(): string {
        return this.getFromStorage('userName');
    }

    set userName(userName: string) {
        this.setToStorage('userName', userName || '');
    }

    get rememberMe(): string {
        return this.getFromStorage('rememberMe');
    }

    set rememberMe(rememberMeVal: boolean) {
        this.setToStorage('rememberMe', rememberMeVal.toString() || '');
    }

    get language(): string {
        return this.getFromStorage('language');
    }

    set language(language: string) {
        this.setToStorage('language', language || '');
    }

    get lastCheckedDate(): string {
        return this.getFromStorage('lastCheckedDate');
    }

    set lastCheckedDate(lastCheckedDate: string) {
        this.setToStorage('lastCheckedDate', lastCheckedDate || '');
    }

    get agentCode(): string {
        return this.getFromStorage('agentCode');
    }

    set agentCode(value: string) {
        this.setToStorage('agentCode', value || '');
    }

    get planId(): string {
        return this.getFromStorage('planId');
    }

    set planId(value: string) {
        this.setToStorage('planId', value || '');
    }

    get taxpodId(): string {
        return this.getFromStorage('taxpodId');
    }

    set taxpodId(value: string) {
        this.setToStorage('taxpodId', value || '');
    }

    get renewType(): string {
        return this.getFromStorage('renewType');
    }

    set renewType(value: string) {
        this.setToStorage('renewType', value || '');
    }

    // Stable per-browser id used by the share-link visit tracker. Stored
    // here (not as a cookie) so it survives cross-site setups where
    // browsers block third-party cookies — the access-token interceptor
    // replays it as the X-Visitor-Id header on every backend request, and
    // the share-visit endpoint seeds it the first time it's missing.
    get clipVisitorId(): string {
        return this.getFromStorage('taxpod_clip_visitor_id');
    }

    set clipVisitorId(value: string) {
        this.setToStorage('taxpod_clip_visitor_id', value || '');
    }

    /**
     * Gets the temporary authorization token used during device limit exceeded flow
     * This token is provided by the backend when a user exceeds device limit during login
     * and allows access to device management without full authentication
     *
     * Following Single Responsibility: Only handles authorize token retrieval
     * SSR Compatible: Uses getFromStorage which handles platform detection
     *
     * @returns The authorize token string, or empty string if not set
     */
    get deviceRemovalAuthorizeToken(): string {
        return this.getFromStorage('deviceRemovalAuthorizeToken');
    }

    /**
     * Sets the temporary authorization token used during device limit exceeded flow
     * Stores token in localStorage for persistence across page navigations
     * Should be cleared after successful re-authentication or session timeout
     *
     * Following Single Responsibility: Only handles authorize token storage
     * SSR Compatible: Uses setToStorage which handles platform detection
     *
     * @param value - The temporary authorization token from device limit error
     */
    set deviceRemovalAuthorizeToken(value: string) {
        this.setToStorage('deviceRemovalAuthorizeToken', value || '');
    }

    setFromLogin(input: LoginResponse, device: Device): void {
        this.accessToken = input.accessToken;
        this.refreshToken = input.refreshToken;
        this.userName = input.userName;
        this.userId = input.id;
        this.deviceNo = device.deviceNo;
        this.registrationId = device.registrationId;
    }

    setRememberMeValue(isRememberMe: boolean): void {
        this.rememberMe = isRememberMe;
    }

    setLanguage(language: string): void {
        this.language = language;
    }

    setLastCheckedDate(lastCheckedDate: string): void {
        this.lastCheckedDate = lastCheckedDate;
    }

    clear(): void {
        this.userName = '';
        this.userId = '';
        this.lastCheckedDate = '';

        // Remove from localStorage safely
        this.removeFromStorage('accessToken');
        this.removeFromStorage('refreshToken');

        this.removeFromStorage('id');
        this.removeFromStorage('registrationId');
        // --------------------------
        // Developer Note (Post Linked-Device Implementation):
        // Do not remove this from local storage
        // to retain data persistence for randomly generated browser device UUID
        // that gets sent to the back-end API server on login API.
        // This is currently being utilized in our Linked Device feature to allow devices to be
        // re-traced after prev. session expiration.
        // Refer to "users.services.ts" - "login" function.
        // this.removeFromStorage('deviceNo');
        // --------------------------
        this.removeFromStorage('userName');
        this.removeFromStorage('rememberMe');

        this.removeFromStorage('language');
        this.removeFromStorage('lastCheckedDate');

        // Clear email verification cooldown to prevent timer bleeding across accounts
        this.removeFromStorage('email_verification_cooldown_expiry');

        // Clear temporary authorize token from device limit flow
        // This ensures the token is removed on logout or session timeout
        this.removeFromStorage('deviceRemovalAuthorizeToken');

        // Clear email verification cooldown to prevent timer bleeding across accounts
        this.removeFromStorage('email_verification_cooldown_expiry');

        // Drop the share-link visitor id so the next anonymous session
        // starts fresh. Otherwise a different person logging in on the
        // same browser would inherit (and AttributeAnonymousShareVisits
        // would re-attribute) the previous user's anonymous visit rows.
        this.removeFromStorage('taxpod_clip_visitor_id');

        this.cookieService.delete('accessToken', '/', '', true, 'Strict');
        this.cookieService.delete('refreshToken', '/', '', true, 'Strict');
    }

    clearPurchaseStorage():void{
        this.removeFromStorage('agentCode');
        this.removeFromStorage('planId');
        this.removeFromStorage('taxpodId');
        this.removeFromStorage('renewType');
    }

    clearLinkedDeviceAuthorizationTokenStorage(): void {
        this.removeFromStorage('deviceRemovalAuthorizeToken')
    }

    clearClipVisitorId(): void {
        this.removeFromStorage('taxpod_clip_visitor_id');
    }
}
